import { useState } from 'react';
import WelcomeStep from './components/steps/WelcomeStep';
import TestStep from './components/steps/TestStep';
import ResultStep from './components/steps/ResultStep';
import { getTestQuestions, getTestConfig } from './tests';
import { calculateScore } from './utils/scoring';
import { getAnalysis } from './utils/analysis';
import { generateAssessmentPDF } from './utils/pdfGenerator';

function App() {
  const [step, setStep] = useState('welcome'); // 'welcome' | 'test' | 'result'
  const [details, setDetails] = useState({ patient: '', dob: '', doctor: '', testType: 'BDI' });
  const [answers, setAnswers] = useState([]);
  const [currentQ, setCurrentQ] = useState(0);

  const handleStart = () => {
    if (!details.patient || !details.dob || !details.doctor) return;
    setAnswers([]);
    setCurrentQ(0);
    setStep('test');
  };

  const handleBack = () => {
    if (currentQ > 0) {
      const newAnswers = [...answers];
      newAnswers.pop(); // Remove last answer
      setAnswers(newAnswers);
      setCurrentQ(currentQ - 1);
    } else {
      setStep('welcome');
    }
  };

  const handleAnswer = (score) => {
    const newAnswers = [...answers, score];
    setAnswers(newAnswers);

    const testData = getTestQuestions(details.testType);
    const totalQs = testData.questions.length;
    const currentQuestion = testData.questions[currentQ];

    // Gate question early termination (e.g. PC-PTSD-5)
    if (currentQuestion.questionType === 'gate_yes_no' && score === 0) {
      setStep('result');
      return;
    }

    if (currentQ < totalQs - 1) {
      setTimeout(() => setCurrentQ(currentQ + 1), 200);
    } else {
      setStep('result');
    }
  };

  const handleGeneratePDF = async () => {
    const questions = getTestQuestions(details.testType);
    const config = getTestConfig(details.testType);
    // CAARS/PID-5 scoring reads questions.config.subscales / .facets. The nested
    // structured data actually lives in config.json under `config.config`, so
    // merge it onto the questions object before scoring.
    const scoringInput = { ...questions, config: config.config || {} };
    const score = calculateScore(answers, details.testType, scoringInput);
    const analysis = getAnalysis(score, config, details.testType);
    await generateAssessmentPDF(details, score, analysis, details.testType, answers, questions, config);
  };

  const handleNewAssessment = () => {
    setStep('welcome');
    setAnswers([]);
    setCurrentQ(0);
  };

  // Render appropriate step
  if (step === 'welcome') {
    return (
      <WelcomeStep
        details={details}
        onDetailsChange={setDetails}
        onStart={handleStart}
      />
    );
  }

  if (step === 'test') {
    const testData = getTestQuestions(details.testType);
    const currentQuestion = testData.questions[currentQ];

    return (
      <TestStep
        testType={details.testType}
        currentQuestion={currentQ}
        totalQuestions={testData.questions.length}
        questionData={currentQuestion}
        onAnswer={handleAnswer}
        onBack={handleBack}
      />
    );
  }

  if (step === 'result') {
    const questions = getTestQuestions(details.testType);
    const config = getTestConfig(details.testType);
    const scoringInput = { ...questions, config: config.config || {} };
    const score = calculateScore(answers, details.testType, scoringInput);
    const analysis = getAnalysis(score, config, details.testType);

    return (
      <ResultStep
        score={score}
        testType={details.testType}
        patientDetails={details}
        analysis={analysis}
        config={config}
        onGeneratePDF={handleGeneratePDF}
        onNewAssessment={handleNewAssessment}
      />
    );
  }

  return null;
}

export default App;
